import { useEffect, useState } from "react";
import { api } from "../lib/api";

export default function Staff() {
    const [staff, setStaff] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const [editingId, setEditingId] = useState(null);

    const [form, setForm] = useState({
        name: "",
        email: "",
        password: "",
    });

    async function loadStaff() {
        try {
            setLoading(true);

            const res = await api.get("/admin/staff");

            setStaff(res.data);
        } catch (error) {
            console.error("Failed to load staff:", error);

            alert(
                error?.response?.data?.detail ||
                "Failed to load sales staff"
            );
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        loadStaff();
    }, []);

    function resetForm() {
        setForm({
            name: "",
            email: "",
            password: "",
        });

        setEditingId(null);
    }

    function handleChange(e) {
        const { name, value } = e.target;

        setForm((prev) => ({
            ...prev,
            [name]: value,
        }));
    }

    async function handleSubmit(e) {
        e.preventDefault();

        if (!form.name.trim()) {
            alert("Please enter the staff name");
            return;
        }

        if (!form.email.trim()) {
            alert("Please enter the email");
            return;
        }

        if (!editingId && !form.password) {
            alert("Please enter a password");
            return;
        }

        try {
            setSaving(true);

            if (editingId) {
                const data = {
                    name: form.name,
                    email: form.email,
                };

                if (form.password.trim()) {
                    data.password = form.password;
                }

                await api.put(
                    `/admin/staff/${editingId}`,
                    data
                );
            } else {
                await api.post(
                    "/admin/staff",
                    {
                        name: form.name,
                        email: form.email,
                        password: form.password,
                    }
                );
            }

            resetForm();

            await loadStaff();
        } catch (error) {
            console.error("Failed to save staff:", error);

            alert(
                error?.response?.data?.detail ||
                "Failed to save sales staff"
            );
        } finally {
            setSaving(false);
        }
    }

    function handleEdit(member) {
        setEditingId(member.id);

        setForm({
            name: member.name || "",
            email: member.email || "",
            password: "",
        });

        window.scrollTo({
            top: 0,
            behavior: "smooth",
        });
    }

    async function handleDelete(member) {
        const confirmed = window.confirm(
            `Are you sure you want to delete "${member.name}"?`
        );

        if (!confirmed) {
            return;
        }

        try {
            await api.delete(
                `/admin/staff/${member.id}`
            );

            if (editingId === member.id) {
                resetForm();
            }

            await loadStaff();
        } catch (error) {
            console.error("Failed to delete staff:", error);

            alert(
                error?.response?.data?.detail ||
                "Failed to delete sales staff"
            );
        }
    }

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-[400px]">
                <div className="text-lg text-slate-600">
                    Loading staff...
                </div>
            </div>
        );
    }

    return (
        <div className="space-y-8">

            {/* Header */}

            <div>
                <h1 className="text-3xl font-bold text-slate-900">
                    Sales Staff
                </h1>

                <p className="mt-2 text-slate-500">
                    Create and manage sales staff accounts.
                </p>
            </div>


            {/* Add / Edit Form */}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">

                <div className="flex items-center justify-between mb-6">

                    <div>
                        <h2 className="text-xl font-semibold text-slate-900">
                            {editingId
                                ? "Edit Sales Staff"
                                : "Add Sales Staff"}
                        </h2>

                        <p className="text-sm text-slate-500 mt-1">
                            {editingId
                                ? "Update the staff account information."
                                : "Create a new sales staff account."}
                        </p>
                    </div>

                    {editingId && (
                        <button
                            type="button"
                            onClick={resetForm}
                            className="text-sm text-slate-600 hover:text-slate-900"
                        >
                            Cancel Edit
                        </button>
                    )}

                </div>


                <form
                    onSubmit={handleSubmit}
                    className="grid grid-cols-1 md:grid-cols-2 gap-5"
                >

                    {/* Name */}

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Name
                        </label>

                        <input
                            type="text"
                            name="name"
                            value={form.name}
                            onChange={handleChange}
                            placeholder="Staff name"
                            className="w-full border border-slate-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-500"
                        />
                    </div>


                    {/* Email */}

                    <div>
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Email
                        </label>

                        <input
                            type="email"
                            name="email"
                            value={form.email}
                            onChange={handleChange}
                            placeholder="staff@example.com"
                            className="w-full border border-slate-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-500"
                        />
                    </div>


                    {/* Password */}

                    <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-slate-700 mb-2">
                            Password
                        </label>

                        <input
                            type="password"
                            name="password"
                            value={form.password}
                            onChange={handleChange}
                            placeholder={
                                editingId
                                    ? "Leave empty to keep current password"
                                    : "Password"
                            }
                            className="w-full border border-slate-300 rounded-lg px-4 py-3 outline-none focus:ring-2 focus:ring-slate-500"
                        />

                        {editingId && (
                            <p className="text-xs text-slate-500 mt-2">
                                Leave empty if you do not want to change the password.
                            </p>
                        )}
                    </div>


                    {/* Buttons */}

                    <div className="md:col-span-2 flex gap-3">

                        <button
                            type="submit"
                            disabled={saving}
                            className="bg-slate-900 text-white px-6 py-3 rounded-lg font-semibold hover:bg-slate-800 disabled:opacity-50"
                        >
                            {saving
                                ? "Saving..."
                                : editingId
                                    ? "Update Staff"
                                    : "Create Staff"}
                        </button>

                        {editingId && (
                            <button
                                type="button"
                                onClick={resetForm}
                                className="border border-slate-300 px-6 py-3 rounded-lg font-semibold text-slate-700 hover:bg-slate-50"
                            >
                                Cancel
                            </button>
                        )}

                    </div>

                </form>

            </div>


            {/* Staff List */}

            <div className="bg-white rounded-xl shadow-sm border border-slate-200 overflow-hidden">

                <div className="px-6 py-5 border-b border-slate-200">

                    <h2 className="text-xl font-semibold text-slate-900">
                        Sales Staff
                    </h2>

                    <p className="text-sm text-slate-500 mt-1">
                        {staff.length} staff account
                        {staff.length !== 1 ? "s" : ""}
                    </p>

                </div>


                {staff.length === 0 ? (

                    <div className="p-10 text-center">

                        <div className="text-lg font-semibold text-slate-700">
                            No sales staff yet
                        </div>

                        <p className="text-sm text-slate-500 mt-2">
                            Create your first sales staff account using the form above.
                        </p>

                    </div>

                ) : (

                    <div className="overflow-x-auto">

                        <table className="w-full">

                            <thead className="bg-slate-50 border-b border-slate-200">

                                <tr>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                                        Name
                                    </th>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                                        Email
                                    </th>

                                    <th className="text-left px-6 py-4 text-sm font-semibold text-slate-700">
                                        Role
                                    </th>

                                    <th className="text-right px-6 py-4 text-sm font-semibold text-slate-700">
                                        Actions
                                    </th>

                                </tr>

                            </thead>


                            <tbody className="divide-y divide-slate-200">

                                {staff.map((member) => (

                                    <tr
                                        key={member.id}
                                        className="hover:bg-slate-50"
                                    >

                                        <td className="px-6 py-4">

                                            <div className="font-medium text-slate-900">
                                                {member.name}
                                            </div>

                                        </td>


                                        <td className="px-6 py-4 text-slate-600">
                                            {member.email}
                                        </td>


                                        <td className="px-6 py-4">

                                            <span className="inline-flex px-3 py-1 rounded-full text-xs font-semibold bg-blue-100 text-blue-700">
                                                Sales
                                            </span>

                                        </td>


                                        <td className="px-6 py-4">

                                            <div className="flex justify-end gap-3">

                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleEdit(member)
                                                    }
                                                    className="text-blue-600 hover:text-blue-800 font-medium"
                                                >
                                                    Edit
                                                </button>


                                                <button
                                                    type="button"
                                                    onClick={() =>
                                                        handleDelete(member)
                                                    }
                                                    className="text-red-600 hover:text-red-800 font-medium"
                                                >
                                                    Delete
                                                </button>

                                            </div>

                                        </td>

                                    </tr>

                                ))}

                            </tbody>

                        </table>

                    </div>

                )}

            </div>

        </div>
    );
}